// Home: today's summary, the to-do list, deals needing attention and the revenue outlook.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router';
import { TypeIcon, useCheckOff } from '../components/CheckOff';
import { StagePill } from '../components/DealBits';
import { CallsMeetingsBlock, GoalsBlock } from '../components/goals/GoalBlocks';
import { useGoals } from '../lib/goals/data';
import { TodaySummary } from '../components/TodaySummary';
import { Card, cx, Empty, ErrorBox, PageHeader, Pill, Spinner, Stat } from '../components/ui';
import { config } from '../config';
import { addDaysISO, date, money, personName, todayISO } from '../lib/format';
import { supabase, unwrap } from '../lib/supabase';
import { loadOpenReminders, ReminderList } from './Reminders';

const STALE_DAYS = 30;
const TODO_PREVIEW = 6;
const STEPS_PREVIEW = 4;

async function loadDashboard() {
  const today = todayISO();
  const [reminders, deals, summary, activity, revenue, sync, counts] = await Promise.all([
    loadOpenReminders(today),
    supabase
      .from('deals')
      .select('id, name, deal_type, stage, next_step, next_step_date, next_step_type, updated_at, contacts!deals_primary_contact_id_fkey(first_name, last_name, display_name)')
      .not('stage', 'in', '(closed,closed_fully_paid)'),
    supabase.from('deal_payment_summary').select('deal_id, overdue_count, next_payment_date, total_commission'),
    supabase.from('deal_activity').select('deal_id, last_activity_at'),
    supabase.from('revenue_projection').select('category, month_start, weighted_amount'),
    supabase.from('sync_state').select('integration, status, last_error, last_error_at').eq('status', 'error'),
    Promise.all([
      supabase.from('contacts').select('id', { count: 'exact', head: true }),
      supabase.from('organizations').select('id', { count: 'exact', head: true }),
      supabase.from('review_queue').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
    ]),
  ]);
  const sums = new Map(unwrap(summary).map((s) => [s.deal_id, s]));
  const lastActivity = new Map(unwrap(activity).map((a) => [a.deal_id, a.last_activity_at]));
  const staleBefore = new Date(Date.now() - STALE_DAYS * 86_400_000).toISOString();
  const attention = unwrap(deals)
    .map((d) => {
      const s = sums.get(d.id);
      const reasons: string[] = [];
      if (Number(s?.overdue_count ?? 0) > 0) reasons.push('payment overdue');
      if (d.next_step_date && d.next_step_date < today) reasons.push('next step late');
      // Stale = no deal edit, no logged activity with the deal or its contact, no payment update.
      const last = lastActivity.get(d.id) ?? d.updated_at;
      if (last < staleBefore) reasons.push(`no activity in ${STALE_DAYS}+ days`);
      return { ...d, reasons, summary: s };
    })
    .filter((d) => d.reasons.length > 0);
  // Open deals' next steps, dated ones first by date, then undated. Every one is in To do;
  // those due in the next 7 days also show under Coming up this week.
  const weekEnd = addDaysISO(7);
  const nextSteps = unwrap(deals)
    .filter((d) => d.next_step)
    .sort((a, b) => (a.next_step_date ?? '9999').localeCompare(b.next_step_date ?? '9999') || a.name.localeCompare(b.name));
  const stepsThisWeek = nextSteps.filter((d) => d.next_step_date && d.next_step_date > today && d.next_step_date <= weekEnd);

  // Revenue: this month + next two, weighted; and received this year.
  const months = [0, 1, 2].map((i) => {
    const dt = new Date();
    dt.setDate(1);
    dt.setMonth(dt.getMonth() + i);
    return dt.toISOString().slice(0, 7);
  });
  const rev = unwrap(revenue);
  const year = today.slice(0, 4);
  return {
    reminders,
    nextSteps,
    stepsThisWeek,
    attention,
    syncErrors: sync.data ?? [],
    outlook: months.map((m) => ({ month: m, total: rev.filter((r) => r.month_start?.startsWith(m)).reduce((t, r) => t + Number(r.weighted_amount), 0) })),
    receivedYtd: rev.filter((r) => r.category === 'received' && r.month_start?.startsWith(year)).reduce((t, r) => t + Number(r.weighted_amount), 0),
    counts: { contacts: counts[0].count ?? 0, companies: counts[1].count ?? 0, review: counts[2].count ?? 0 },
  };
}

export function Dashboard() {
  const { data, isLoading, error } = useQuery({ queryKey: ['dashboard'], queryFn: loadDashboard, refetchInterval: 5 * 60_000 });
  const { checkOffStep } = useCheckOff();
  const firstName = useGoals().data?.firstName;
  const doneStep = (d: NextStepRow) => checkOffStep({ dealId: d.id, type: d.next_step_type, name: d.contacts ? personName(d.contacts) : d.name });
  const today = todayISO();
  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorBox error={error} />;
  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div>
      <PageHeader title={firstName ? `${greeting}, ${firstName}` : greeting} subtitle={new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: config.timezone })} />

      {data.syncErrors.map((s) => (
        <div key={s.integration} className="mb-3 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <AlertTriangle className="h-4 w-4" /> {s.integration} sync is failing since {date(s.last_error_at?.slice(0, 10))}: {s.last_error}
        </div>
      ))}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Due today" value={data.reminders.length} sub={data.reminders.filter((r) => r.due_date < today).length ? `${data.reminders.filter((r) => r.due_date < today).length} overdue` : 'all caught up'} />
        <Stat label="Deals needing attention" value={data.attention.length} />
        <Stat label="Received this year" value={money(data.receivedYtd, false)} />
        <Stat
          label="To review"
          value={
            <Link to="/review" className="hover:text-indigo-700">
              {data.counts.review}
            </Link>
          }
          sub={`${data.counts.contacts.toLocaleString()} contacts · ${data.counts.companies.toLocaleString()} companies`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <TodaySummary />
          <Card title="To do" actions={<Link to="/reminders" className="text-xs text-indigo-600 hover:underline">All reminders</Link>}>
            {/* A preview (Goals spec Phase 0): overdue first, then today; everything is on the Reminders page. */}
            {(data.reminders.length > 0 || data.nextSteps.length === 0) && <ReminderList rows={data.reminders.slice(0, TODO_PREVIEW)} compact />}
            {data.reminders.length > TODO_PREVIEW && (
              <Link to="/reminders" className="mt-1 block text-xs font-medium text-indigo-600 hover:underline">
                View all ({data.reminders.length})
              </Link>
            )}
            <NextStepList rows={data.nextSteps.slice(0, STEPS_PREVIEW)} onDone={doneStep} divider={data.reminders.length > 0} />
            {data.nextSteps.length > STEPS_PREVIEW && (
              <Link to="/reminders" className="mt-1 block text-xs font-medium text-indigo-600 hover:underline">
                View all next steps ({data.nextSteps.length})
              </Link>
            )}
          </Card>
          <Card title="Deals needing attention">
            {data.attention.length === 0 ? (
              <Empty>Nothing overdue or stale.</Empty>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.attention.map((d) => (
                  <li key={d.id} className="py-2">
                    <Link to={`/deals/${d.id}`} className="flex flex-wrap items-center gap-2 hover:text-indigo-700">
                      <span className="font-medium text-slate-900">{d.name}</span>
                      <StagePill stage={d.stage} />
                      {d.reasons.map((r) => (
                        <Pill key={r} color={r.startsWith('payment') ? 'red' : 'orange'}>
                          {r}
                        </Pill>
                      ))}
                    </Link>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {d.contacts && personName(d.contacts)}
                      {d.next_step && ` · next: ${d.next_step}`}
                      {d.next_step_date && ` (${date(d.next_step_date)})`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <GoalsBlock />
        </div>
        <div className="space-y-4">
          <Card title="Revenue outlook" actions={<Link to="/revenue" className="text-xs text-indigo-600 hover:underline">Details</Link>}>
            <ul className="space-y-2 text-sm">
              {data.outlook.map((m) => (
                <li key={m.month} className="flex justify-between">
                  <span className="text-slate-600">{new Date(`${m.month}-15`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                  <span className="font-medium">{money(m.total, false)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-slate-400">Weighted by stage probability; received at 100%.</p>
          </Card>
          <Card title="Coming up this week">
            <UpcomingWeek steps={data.stepsThisWeek} onDone={doneStep} />
          </Card>
          <CallsMeetingsBlock />
        </div>
      </div>
    </div>
  );
}

export type NextStepRow = Awaited<ReturnType<typeof loadDashboard>>['nextSteps'][number];

export function NextStepList({ rows, onDone, divider }: { rows: NextStepRow[]; onDone: (row: NextStepRow) => void; divider: boolean }) {
  const today = todayISO();
  if (rows.length === 0) return null;
  return (
    <>
      <div className={cx('text-xs font-medium text-slate-500', divider && 'mt-3 border-t border-slate-100 pt-2')}>Deal next steps</div>
      <ul className="divide-y divide-slate-100">
        {rows.map((d) => (
          <li key={d.id} className="flex items-start gap-2.5 py-2 text-sm">
            <input type="checkbox" className="mt-1" title="Mark done" aria-label="Mark done" onChange={() => onDone(d)} />
            <TypeIcon type={d.next_step_type} />
            <div className="min-w-0 flex-1">
              <div className="text-slate-800">{d.next_step}</div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500">
                {d.next_step_date && (
                  <span className={d.next_step_date < today ? 'font-medium text-rose-600' : d.next_step_date === today ? 'font-medium text-amber-700' : ''}>
                    {d.next_step_date < today ? `overdue · ${date(d.next_step_date)}` : d.next_step_date === today ? 'today' : date(d.next_step_date)}
                  </span>
                )}
                <Link to={`/deals/${d.id}`} className="text-indigo-600 hover:underline">
                  {d.name}
                </Link>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function UpcomingWeek({ steps, onDone }: { steps: NextStepRow[]; onDone: (row: NextStepRow) => void }) {
  const { data = [] } = useQuery({
    queryKey: ['reminders', 'week'],
    queryFn: async () => (await loadOpenReminders(addDaysISO(7))).filter((r) => r.due_date > todayISO()),
  });
  return (
    <>
      {(data.length > 0 || steps.length === 0) && <ReminderList rows={data} compact />}
      <NextStepList rows={steps} onDone={onDone} divider={data.length > 0} />
    </>
  );
}
